import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useUIStore } from '@/lib/core/stores/useUIStore';
import { randomUuid } from '@/lib/core/utils';
import {
  RangeRoverFlowConfirmationElementContentModelContentType,
  RangeRoverFlowConfirmationPropertiesModel,
  RangeRoverFlowDriverInfoElementContentModelContentType,
  RangeRoverFlowDriverInfoPropertiesModel,
  RangeRoverFlowPaymentElementContentModelContentType,
  RangeRoverFlowWhereFromWhereToWhenElementContentModelContentType,
  RangeRoverFlowWhereFromWhereToWhenPropertiesModel,
  RideDto,
  ServiceCategoriesContentResponseModel,
} from '@/services/platform/api/model';
import type { BookingState, BookFlowRangeRoverProps } from './types';
import { RangeRoverBook } from './book';
import { RangeRoverDriverInfo } from './driverInfo';
import { RangeRoverPayment } from './payment';
import { RangeRoverConfirmation } from './confirmation/confirmation';
import { getServiceCategories } from '@/services/platform/data/booking';
import { useLocaleStore, useActiveRideStore } from '@/lib/core/stores';

export type { BookingState, BookFlowRangeRoverProps };

const makeEmptyBooking = (): BookingState => ({
  pickupPoint: null,
  dropoffPoint: null,
  isScheduled: false,
  date: null,
  time: null,
  idempotencyKey: randomUuid(),
  ride: null,
});

/** A paid ride sits here — priced and recorded, not yet sent to Onde — until the fare is secured. */
const rideNeedsPayment = (ride?: RideDto | null): boolean =>
  ride?.requiresPayment === true && ride?.status === 'AWAITING_PAYMENT';

// ── BookFlowRangeRover ────────────────────────────────────────────────────────

export function BookFlowRangeRover({ content, context }: BookFlowRangeRoverProps) {
  const navigate = useNavigate();
  const locale = useLocaleStore(state => state.locale);
  const setHideNavbar = useUIStore(state => state.setHideNavbar);
  const activeRidePublicGuid = useActiveRideStore(state => state.publicGuid);
  const activeRide = useActiveRideStore(state => state.ride);
  const setActiveRide = useActiveRideStore(state => state.setActiveRide);
  const clearActiveRide = useActiveRideStore(state => state.clearActiveRide);

  const steps = content.properties?.steps?.items ?? [];

  // Looked up by content type rather than hardcoded, since the payment step is conditional and the
  // CMS-authored order of `steps` is what actually decides array position.
  const paymentStepIndex = steps.findIndex(
    s => s.content?.contentType === RangeRoverFlowPaymentElementContentModelContentType.rangeRoverFlowPayment
  );
  const driverInfoStepIndex = steps.findIndex(
    s =>
      s.content?.contentType ===
      RangeRoverFlowDriverInfoElementContentModelContentType.rangeRoverFlowDriverInfo
  );
  const confirmationStepIndex = steps.findIndex(
    s =>
      s.content?.contentType ===
      RangeRoverFlowConfirmationElementContentModelContentType.rangeRoverFlowConfirmation
  );
  const driverInfoStep = driverInfoStepIndex !== -1 ? driverInfoStepIndex : 1;
  const confirmationStep = confirmationStepIndex !== -1 ? confirmationStepIndex : 2;

  // A persisted active ride survives a cold reload (notably the MB WAY redirect round-trip, which
  // reloads the whole app). Resuming into the payment step when the ride is still awaiting payment,
  // rather than always into driver-info, is what lets `CheckoutForm`'s own in-flight-payment recovery
  // (`getActivePayments`) pick a stalled MB WAY payment back up instead of stranding it.
  const [step, setStep] = useState<number>(() => {
    if (!activeRidePublicGuid) return 0;
    return rideNeedsPayment(activeRide)
      ? paymentStepIndex !== -1
        ? paymentStepIndex
        : driverInfoStep
      : driverInfoStep;
  });
  const [ride, setRide] = useState<RideDto | null>(null);
  const [serviceCategory, setServiceCategory] = useState<ServiceCategoriesContentResponseModel | null>(null);

  // Re-asserted on every step change, not just on mount: the payment step's `CheckoutScreen` owns
  // this same flag for its other (non-embedded) uses and clears it on unmount, which would otherwise
  // leave the navbar visible after navigating away from — or to — the payment step. Only cleared
  // here when the whole flow unmounts.
  useEffect(() => {
    setHideNavbar(true);
  }, [setHideNavbar, step]);
  useEffect(() => {
    return () => setHideNavbar(false);
  }, [setHideNavbar]);
  const [booking, setBooking] = useState<BookingState>(() => {
    const base = makeEmptyBooking();
    return activeRidePublicGuid && activeRide ? { ...base, ride: activeRide } : base;
  });

  // The service category picker stub only carries `id` (`properties: {}`) — resolve the
  // full content (images, hasPayment) via getServiceCategories, mirroring BookIntroEngine.
  useEffect(() => {
    const stubs = content?.properties?.serviceCategory ?? [];
    if (!stubs.length) return;
    getServiceCategories(stubs, locale).then(items => setServiceCategory(items[0] ?? null));
  }, [content?.properties?.serviceCategory, locale]);

  const handleBack = () => {
    if (step === 0) navigate(-1);
    else setStep(prev => prev - 1);
  };

  return (
    <div className="pt-safe-area relative flex h-full flex-col bg-white">
      {steps.map((stepItem, index) => {
        if (index !== step) return null;
        const props = stepItem.content?.properties;
        const stepType = stepItem.content?.contentType;
        switch (stepType) {
          case RangeRoverFlowWhereFromWhereToWhenElementContentModelContentType.rangeRoverFlowWhereFromWhereToWhen:
            return (
              <RangeRoverBook
                key={index}
                cmsProps={props as RangeRoverFlowWhereFromWhereToWhenPropertiesModel}
                booking={booking}
                onBookingChange={updates => setBooking(prev => ({ ...prev, ...updates }))}
                onBack={handleBack}
                onBookNow={res => {
                  setBooking(prev => ({ ...prev, ride: res }));
                  setRide(res);
                  if (rideNeedsPayment(res) && paymentStepIndex !== -1) {
                    // Temporarily tracked so a scheduled ride's payment also survives an MB WAY
                    // reload — cleared again once payment succeeds, same as the non-payment path.
                    setActiveRide(res);
                    setStep(paymentStepIndex);
                    return;
                  }
                  // Scheduled rides aren't tracked in local storage — that's only for
                  // live/ASAP rides (see `useActiveRideTracking`). Clear defensively in
                  // case an unrelated ride was already being tracked.
                  clearActiveRide();
                  setStep(confirmationStep);
                }}
                onRideCreated={res => {
                  setBooking(prev => ({ ...prev, ride: res }));
                  setActiveRide(res);
                  if (rideNeedsPayment(res) && paymentStepIndex !== -1) {
                    setStep(paymentStepIndex);
                    return;
                  }
                  setStep(driverInfoStep);
                }}
                context={context}
                serviceCategory={serviceCategory}
                step={index + 2}
              />
            );
          case RangeRoverFlowPaymentElementContentModelContentType.rangeRoverFlowPayment: {
            if (!booking.ride) return null;
            const isAsap = !(booking.isScheduled && booking.date && booking.time);
            return (
              <RangeRoverPayment
                key={index}
                booking={booking}
                context={context}
                onBack={handleBack}
                onSuccess={paidRide => {
                  setBooking(prev => ({ ...prev, ride: paidRide }));
                  if (isAsap) {
                    setActiveRide(paidRide);
                    setStep(driverInfoStep);
                    return;
                  }
                  setRide(paidRide);
                  clearActiveRide();
                  setStep(confirmationStep);
                }}
                step={index + 2}
              />
            );
          }
          case RangeRoverFlowDriverInfoElementContentModelContentType.rangeRoverFlowDriverInfo:
            return (
              <RangeRoverDriverInfo
                key={index}
                cmsProps={props as RangeRoverFlowDriverInfoPropertiesModel}
                booking={booking}
                onBack={handleBack}
                onGoHome={() => navigate('/')}
                onSuccess={res => {
                  setBooking(prev => ({ ...prev, ride: res }));
                  setRide(res);
                  setStep(confirmationStep);
                }}
                step={index + 2}
              />
            );
          case RangeRoverFlowConfirmationElementContentModelContentType.rangeRoverFlowConfirmation:
            if (!ride) return null;
            return (
              <RangeRoverConfirmation
                key={index}
                booking={booking}
                ride={ride}
                cmsProps={props as RangeRoverFlowConfirmationPropertiesModel}
                serviceCategory={serviceCategory}
                onGoHome={() => navigate('/')}
                onSeeRideDetails={() =>
                  navigate(`${context?.settings?.properties?.bookingDetail?.route?.path}/${ride.publicGuid}`)
                }
              />
            );
          default:
            return null;
        }
      })}
    </div>
  );
}
